/**
 * SaveSnapshotButton — reusable green "Save snapshot" pill.
 *
 * Any form (Inventory, Checklist, Permits, Insurer Assets, Subcontractor
 * report) can drop this in and pass:
 *   • kind            — must be one of the 5 supported snapshot kinds.
 *   • getPayload      — callback returning the JSON-serialisable state to
 *                       freeze. Called only after the user confirms a name.
 *   • ownerNodeId     — optional, scopes the snapshot to a workspace node
 *                       so the "Saved Files" dialog can group it visually.
 *   • defaultTitle    — pre-fills the prompt; user can edit.
 *
 * Backend route: POST /api/admin/snapshots.
 */
import axios from 'axios';
import { Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaveSnapshotButton({
  kind,
  getPayload,
  ownerNodeId = null,
  defaultTitle,
  testid = 'save-snapshot-btn',
  label = 'Save snapshot',
}) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  if (!adminToken) return null;

  const handleSave = async () => {
    const seed = defaultTitle || `${kind} · ${new Date().toLocaleDateString()}`;
    const title = window.prompt('Name this saved snapshot:', seed);
    if (!title) return;
    try {
      const payload = await Promise.resolve(getPayload());
      await axios.post(
        `${API}/admin/snapshots`,
        { kind, title, owner_node_id: ownerNodeId, payload, shared: false },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      alert(`Saved "${title}" to My Files. Open it any time from the green folder pill in the header.`);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save snapshot');
    }
  };

  return (
    <button
      type="button"
      onClick={handleSave}
      data-testid={testid}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:-translate-y-0.5"
      style={{
        backgroundColor: '#22C55E20',
        color: '#15803D',
        border: '1px solid #BBF7D0',
        fontFamily: 'Manrope',
      }}
      title="Save the current form as a snapshot in My Files"
    >
      <Save className="w-3 h-3" /> {label}
    </button>
  );
}
